import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { MoreHorizontal, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { DataTable, createSortableHeader } from "../../../components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useOperations } from "../../operations-management";
import { UserRolesDialog } from "../../role-management/components/user-roles-dialog";
import { DeleteUserDialog } from "../components/delete-user-dialog";
import { UserFormDialog } from "../components/user-form-dialog";
import { useUsers } from "../hooks/use-user-queries";
import { OperationConfig, OperationFilter, User } from "../types";

const ALL_OPERATIONS_FILTER: OperationConfig = {
  value: "all",
  label: "Todas as Operações",
  color: "",
};

export default function UserManagementPage() {
  const [selectedOps, setSelectedOps] = useState<OperationFilter>("all");
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRolesDialogOpen, setIsRolesDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const { data: operations = [] } = useOperations();
  const { data: users = [], isLoading, refetch } = useUsers(selectedOps);

  const availableOperations = useMemo<OperationConfig[]>(() => {
    return operations.map((op) => ({
      value: op.ops,
      label: op.displayName,
      color: op.color,
    }));
  }, [operations]);

  const operationsWithAll = useMemo<OperationConfig[]>(() => {
    return [ALL_OPERATIONS_FILTER, ...availableOperations];
  }, [availableOperations]);

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsFormDialogOpen(true);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const handleManageRoles = (user: User) => {
    setSelectedUser(user);
    setIsRolesDialogOpen(true);
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setIsFormDialogOpen(true);
  };

  const handleFormClose = () => {
    setIsFormDialogOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteClose = () => {
    setIsDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const getOpsLabel = () => {
    if (selectedOps === "all") {
      return `Mostrando todos os ${users.length} usuários`;
    }

    const selectedOpsLabel = operationsWithAll.find(
      (o) => o.value === selectedOps
    )?.label;
    const userCount = users.length;
    const userText = userCount !== 1 ? "usuários" : "usuário";

    return `${userCount} ${userText} com acesso à ${selectedOpsLabel}`;
  };

  const columns: ColumnDef<User>[] = [
    {
      accessorKey: "name",
      header: createSortableHeader("Nome"),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2">
            <div className="font-medium">{row.original.name}</div>
          </div>
        );
      },
    },
    {
      accessorKey: "email",
      header: createSortableHeader("E-mail"),
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground">
          {row.original.email}
        </div>
      ),
    },
    {
      accessorKey: "permissions",
      header: "Permissões",
      enableSorting: false,
      cell: ({ row }) => {
        const permissions = row.original.permissions;

        return (
          <div className="flex flex-wrap gap-1">
            {permissions.map((ops) => {
              const opsConfig = availableOperations.find(
                (o) => o.value === ops
              );
              const label =
                opsConfig?.label ??
                ops.charAt(0).toUpperCase() + ops.slice(1);
              return (
                <Badge
                  key={ops}
                  variant="outline"
                  className="text-xs"
                  style={
                    opsConfig?.color
                      ? { borderColor: opsConfig.color, color: opsConfig.color }
                      : {}
                  }
                >
                  {label}
                </Badge>
              );
            })}
          </div>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const user = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Abrir Menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Ações</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleEditUser(user)}>
                Editar usuário
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleManageRoles(user)}>
                Gerenciar funções
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleDeleteUser(user)}
                className="text-red-600"
              >
                Excluir usuário
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="w-full py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Gerenciamento de Usuários
          </h1>
          <p className="text-muted-foreground">
            Gerencie os usuários e suas permissões
          </p>
        </div>
        <Button onClick={handleAddUser}>
          <Plus className="mr-2 h-4 w-4" />
          Adicionar Usuário
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Filtrar por Operações:</label>
          <Select
            value={selectedOps}
            onValueChange={(value) => setSelectedOps(value as OperationFilter)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select operation" />
            </SelectTrigger>
            <SelectContent>
              {operationsWithAll.map((ops) => (
                <SelectItem key={ops.value} value={ops.value}>
                  {ops.value !== "all" && ops.color && (
                    <span
                      className="inline-block w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: ops.color }}
                    />
                  )}
                  {ops.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm text-muted-foreground">{getOpsLabel()}</div>
      </div>

      <DataTable
        columns={columns}
        data={users}
        loading={isLoading}
        onRefresh={refetch}
        searchPlaceholder="Pesquise por nome ou e-mail..."
        searchableColumns={["name", "email"]}
        enableExport
        exportFileName={`users-${selectedOps}-${format(new Date(), "yyyy-MM-dd")}`}
      />

      <UserFormDialog
        open={isFormDialogOpen}
        onOpenChange={setIsFormDialogOpen}
        user={selectedUser}
        onSuccess={handleFormClose}
        operations={availableOperations}
      />

      <DeleteUserDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        user={selectedUser}
        onSuccess={handleDeleteClose}
      />

      <UserRolesDialog
        open={isRolesDialogOpen}
        onOpenChange={setIsRolesDialogOpen}
        userId={selectedUser?.id ?? null}
        userName={selectedUser?.name}
        opsList={selectedUser?.permissions ?? []}
        onSuccess={() => setSelectedUser(null)}
      />
    </div>
  );
}
